import { useState, useCallback, useEffect } from 'react'
import type { Layout } from 'react-grid-layout'

export interface PinnedCard {
  id: string
  question: string
  answer: string
  chart?: unknown
  eventId?: number | null
  pinnedAt: string
  layout: Layout
}

const STORAGE_KEY = 'butler-buddy-pinned-cards'

function isValidCard(c: unknown): c is PinnedCard {
  if (!c || typeof c !== 'object') return false
  const obj = c as Record<string, unknown>
  return (
    typeof obj.id === 'string' &&
    typeof obj.question === 'string' &&
    typeof obj.answer === 'string' &&
    typeof obj.pinnedAt === 'string' &&
    obj.layout != null &&
    typeof obj.layout === 'object' &&
    typeof (obj.layout as Record<string, unknown>).w === 'number' &&
    typeof (obj.layout as Record<string, unknown>).h === 'number'
  )
}

function loadCards(): PinnedCard[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(isValidCard)
  } catch {
    return []
  }
}

function saveCards(cards: PinnedCard[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(cards))
}

let idCounter = 0

function nextY(cards: PinnedCard[]): number {
  if (cards.length === 0) return 0
  return Math.max(...cards.map((c) => c.layout.y + c.layout.h))
}

export function usePinnedCards() {
  const [cards, setCards] = useState<PinnedCard[]>(loadCards)

  useEffect(() => {
    saveCards(cards)
  }, [cards])

  const pinCard = useCallback(
    (question: string, answer: string, chart?: unknown, eventId?: number | null) => {
      setCards((prev) => {
        const id = `pin_${Date.now().toString(36)}_${++idCounter}`
        const y = nextY(prev)
        const card: PinnedCard = {
          id,
          question,
          answer,
          chart,
          eventId: eventId ?? null,
          pinnedAt: new Date().toISOString(),
          layout: { i: id, x: (prev.length % 2) * 6, y, w: 6, h: 4 },
        }
        return [...prev, card]
      })
    },
    []
  )

  const unpinCard = useCallback((id: string) => {
    setCards((prev) => prev.filter((c) => c.id !== id))
  }, [])

  const updateLayouts = useCallback((layouts: Layout[]) => {
    const layoutMap = new Map(layouts.map((l) => [l.i, l]))
    setCards((prev) =>
      prev.map((card) => {
        const updated = layoutMap.get(card.id)
        return updated ? { ...card, layout: updated } : card
      })
    )
  }, [])

  return { cards, pinCard, unpinCard, updateLayouts }
}
